// src/pages/TermsPage.js
//
// Terms of Service. Layout chrome lives in <LegalPageShell>.

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalPageShell from '../components/LegalPageShell';

const TermsPage = () => {
  useEffect(() => {
    document.title = 'Terms of Service | Nexus';
  }, []);

  return (
    <LegalPageShell title="Terms of Service" updated="January 7, 2026">
      <h2>1. Acceptance of Terms</h2>
      <p>
        By accessing and using Nexus ("the Service"), you agree to be bound by these Terms of Service.
        If you do not agree to these terms, please do not use the Service.
      </p>

      <h2>2. Description of Service</h2>
      <p>
        Nexus is an inventory management and business tracking platform designed for resellers.
        The Service allows you to track inventory, log sales, manage expenses, and generate reports
        to help you run your resale business.
      </p>

      <h2>3. Eligibility</h2>
      <p>
        You must be at least 18 years old to use this Service. By using the Service, you represent
        and warrant that you are at least 18 years of age and have the legal capacity to enter into these Terms.
      </p>

      <h2>4. Account Registration</h2>
      <p>To use certain features of the Service, you must register for an account. You agree to:</p>
      <ul>
        <li>Provide accurate and complete registration information</li>
        <li>Maintain the security of your password</li>
        <li>Accept responsibility for all activities under your account</li>
        <li>Notify us immediately of any unauthorized use of your account</li>
      </ul>

      <h2>5. Subscription and Payments</h2>
      <p>Nexus offers a free tier and paid subscription plans. By subscribing to a paid plan:</p>
      <ul>
        <li>You authorize us to charge your payment method on a recurring basis</li>
        <li>Subscription fees are billed in advance on a monthly basis</li>
        <li>You can cancel your subscription at any time through your account settings</li>
        <li>Upon cancellation, you will retain access to paid features until the end of your current billing period</li>
        <li>No refunds are provided for partial billing periods</li>
        <li>Refunds for the current billing period may be requested within 7 days of charge by contacting support</li>
        <li>We reserve the right to change subscription pricing with 30 days notice to existing subscribers</li>
      </ul>

      <h2>6. User Content</h2>
      <p>
        You retain ownership of all data and content you enter into the Service. By using the Service,
        you grant us a license to store, process, and display your content solely for the purpose of
        providing the Service to you.
      </p>
      <p>
        You are solely responsible for the accuracy, completeness, and legality of all data you enter
        into the Service. Nexus is not responsible for errors, omissions, or decisions made based on user-entered data.
      </p>

      <h2>7. Financial Disclaimer</h2>
      <p className="important-notice">
        <strong>IMPORTANT:</strong> Nexus provides tools for tracking and calculating inventory, sales, profit,
        and other business metrics. All financial calculations, reports, projections, and estimates are for
        informational purposes only.
      </p>
      <p>
        Nexus is not a financial advisor, accountant, or tax professional. We make no guarantees regarding
        the accuracy of calculations, profit estimates, market valuations, or any other financial data
        displayed within the Service.
      </p>
      <p>Users are solely responsible for:</p>
      <ul>
        <li>Verifying all financial data and calculations</li>
        <li>Making their own business and financial decisions</li>
        <li>Consulting appropriate professionals (accountants, tax advisors, attorneys) for financial, tax, or legal advice</li>
        <li>Maintaining their own records for tax and legal purposes</li>
      </ul>
      <p>
        We expressly disclaim any liability for financial losses, tax penalties, or other damages arising
        from reliance on data or calculations provided by the Service.
      </p>

      <h2>8. Third-Party Platforms and Integrations</h2>
      <p>
        The Service may integrate with, display data from, or link to third-party platforms including
        but not limited to eBay, StockX, Mercari, GOAT, TCGPlayer, and other marketplaces or pricing services.
      </p>
      <p>
        We are not affiliated with, endorsed by, or responsible for any third-party platforms. We do not guarantee:
      </p>
      <ul>
        <li>The accuracy, completeness, or timeliness of third-party data</li>
        <li>Continued availability of any third-party integrations</li>
        <li>Compatibility with changes to third-party APIs or services</li>
      </ul>
      <p>
        Your use of third-party services is subject to their respective terms of service and privacy policies.
        Nexus is not responsible for any actions you take on third-party platforms or any consequences thereof.
      </p>

      <h2>9. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any illegal purpose</li>
        <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
        <li>Interfere with or disrupt the Service or its infrastructure</li>
        <li>Upload malicious code, viruses, or harmful content</li>
        <li>Use the Service to send spam or unsolicited communications</li>
        <li>Resell, redistribute, or sublicense the Service without authorization</li>
        <li>Use automated scripts or bots to access the Service without permission</li>
        <li>Circumvent any security features or access restrictions</li>
        <li>Use the Service in any manner that could damage, disable, or impair the Service</li>
      </ul>

      <h2>10. Service Availability</h2>
      <p>
        We strive to maintain high availability of the Service but do not guarantee uninterrupted,
        error-free, or secure access at all times.
      </p>
      <p>The Service may be temporarily unavailable due to:</p>
      <ul>
        <li>Scheduled maintenance (communicated in advance when possible)</li>
        <li>Unscheduled emergency maintenance</li>
        <li>Factors outside our control (internet outages, third-party service failures, etc.)</li>
      </ul>
      <p>
        We are not liable for any losses, damages, or inconvenience resulting from service downtime or unavailability.
      </p>

      <h2>11. Intellectual Property</h2>
      <p>
        The Service and its original content, features, functionality, branding, and design are owned
        by Nexus and are protected by international copyright, trademark, and other intellectual property laws.
      </p>
      <p>
        You may not copy, modify, distribute, sell, or lease any part of the Service without our prior written consent.
      </p>

      <h2>12. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Nexus and its owners, officers, employees, and agents
        shall not be liable for any indirect, incidental, special, consequential, or punitive damages,
        including but not limited to:
      </p>
      <ul>
        <li>Loss of profits, revenue, or business opportunities</li>
        <li>Loss of data or data corruption</li>
        <li>Business interruption</li>
        <li>Errors in financial calculations or reports</li>
        <li>Decisions made based on information provided by the Service</li>
        <li>Any other intangible losses</li>
      </ul>
      <p>
        Our total liability for any claims arising from your use of the Service shall not exceed the
        amount you paid to us in the twelve (12) months preceding the claim.
      </p>

      <h2>13. Disclaimer of Warranties</h2>
      <p>
        The Service is provided "as is" and "as available" without warranties of any kind, either express
        or implied, including but not limited to implied warranties of merchantability, fitness for a
        particular purpose, accuracy, and non-infringement.
      </p>
      <p>We do not warrant that:</p>
      <ul>
        <li>The Service will meet your specific requirements</li>
        <li>The Service will be uninterrupted, timely, secure, or error-free</li>
        <li>Any calculations, reports, or data will be accurate or reliable</li>
        <li>Any errors in the Service will be corrected</li>
      </ul>

      <h2>14. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless Nexus and its owners, officers, employees,
        and agents from any claims, damages, losses, liabilities, costs, and expenses (including reasonable
        attorney fees) arising from:
      </p>
      <ul>
        <li>Your use of the Service</li>
        <li>Your violation of these Terms</li>
        <li>Your violation of any rights of another party</li>
        <li>Any content you submit to the Service</li>
      </ul>

      <h2>15. Termination</h2>
      <p>
        We may terminate or suspend your account at any time for any reason, including violation of these
        Terms, with or without notice. Upon termination:
      </p>
      <ul>
        <li>Your right to use the Service will immediately cease</li>
        <li>We may delete your account and associated data after a reasonable retention period</li>
        <li>Provisions of these Terms that by their nature should survive termination will survive</li>
      </ul>
      <p>
        You may terminate your account at any time through your account settings or by contacting support.
      </p>

      <h2>16. Changes to Terms</h2>
      <p>We reserve the right to modify these Terms at any time. We will notify you of material changes by:</p>
      <ul>
        <li>Posting the new Terms on this page</li>
        <li>Updating the "Last updated" date</li>
        <li>Sending an email notification for significant changes</li>
      </ul>
      <p>
        Your continued use of the Service after changes constitutes acceptance of the new Terms.
        If you do not agree to the modified Terms, you must stop using the Service.
      </p>

      <h2>17. Governing Law and Disputes</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the United States,
        without regard to its conflict of law provisions.
      </p>
      <p>
        Any disputes arising from these Terms or your use of the Service shall be resolved through binding
        arbitration, except that either party may seek injunctive relief in court for intellectual property
        violations or unauthorized access to the Service.
      </p>
      <p>
        You agree to resolve disputes on an individual basis and waive any right to participate in class
        action lawsuits or class-wide arbitration.
      </p>

      <h2>18. Severability</h2>
      <p>
        If any provision of these Terms is found to be unenforceable or invalid, that provision shall be
        limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain
        in full force and effect.
      </p>

      <h2>19. Entire Agreement</h2>
      <p>
        These Terms constitute the entire agreement between you and Nexus regarding the Service and
        supersede any prior agreements.
      </p>

      <h2>20. Contact Us</h2>
      <p>
        If you have any questions about these Terms of Service, please contact us through our <Link to="/support">Support page</Link>.
      </p>
    </LegalPageShell>
  );
};

export default TermsPage;
